import usePrivateAxios from '@/common/api/usePrivateAxios';
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant';
import { logRelatedTypes } from '@/common/constants/logRelatedTypes';
import { Mapper } from '@/common/services/mapper';
import { MetaResponse } from '@/common/types/metaResponse';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

type UseLogChanges<T> = {
  filters?: {
    search?: string;
    page?: number;
    changeHistoryTypeKey?: keyof typeof logRelatedTypes;
    relatedToOrItemId?: string;
    createdAtGt?: string;
    createdAtLt?: string;
  };
  mapper?: (data: any) => T;
};

const useLogChanges = <T,>({
  filters: { search = '', page, changeHistoryTypeKey, relatedToOrItemId, createdAtGt, createdAtLt } = {},
  mapper = Mapper.default<T>(),
}: UseLogChanges<T>) => {
  const privateAxios = usePrivateAxios()

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    isPending,
    ...rest
  } = useQuery({
    queryKey: ['logChanges', search, page, changeHistoryTypeKey, relatedToOrItemId, createdAtGt, createdAtLt],
    queryFn: () => {
      return privateAxios.get(`/log/changes`, {
        params: {
          search,
          page,
          'q[related_to_or_item_type]': logRelatedTypes[changeHistoryTypeKey as keyof typeof logRelatedTypes] || changeHistoryTypeKey,
          'q[related_to_or_item_id]': relatedToOrItemId,
          'q[created_at_gteq]': createdAtGt,
          'q[created_at_lteq]': createdAtLt,
        },
      });
    },
    select: (data: { data: { data: any[]; meta: MetaResponse; }; }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      };
    },
    placeholderData: keepPreviousData,
  });

  return { data, meta, isPending, ...rest };
};

export default useLogChanges;
